import React, {ComponentProps, useEffect, useRef, useState} from "react";
import {twMerge} from "tailwind-merge";
import {If} from "../If";
import {UserMedia} from "./useUserMeia";
import {imageBaseUrl} from "../Utils/variables";
import {Modal} from "../Modal";

export interface CameraProps extends ComponentProps<"div"> {
  previewImage?: string;
  onValueChange: (image: string) => void;
}

export interface StartCameraProps extends ComponentProps<"div"> {
  onClose: () => void;
}

export interface ContextProps {
  setImage: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const CreateContext = React.createContext<ContextProps>({
  setImage: () => null,
});

const useContext = () => React.useContext(CreateContext);

const StartCamera = ({onClose}: StartCameraProps) => {
  const {mediaStream, error} = UserMedia();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasElementRef = useRef<HTMLCanvasElement | null>(null);
  const {setImage} = useContext();
  useEffect(() => {
    if (mediaStream && videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = mediaStream;
    }
  }, [mediaStream]);
  const handleCanPlay = () => {
    if (videoRef.current) videoRef.current.play();
  };
  const handleClickPicture = () => {
    const video = videoRef.current;
    const canvas = canvasElementRef.current;
    if (canvas && video) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx?.drawImage(video, 0, 0);
      setImage(canvas.toDataURL("image/png"));
      onClose();
    }
  };
  return (
    <Modal
      className="items-start mt-5 z-50"
      toggleModalSize={() => null}
      active={true}
      maximize={false}
      onCloseHandler={onClose}
    >
      <Modal.Content className="p-0">
        <Modal.ContentHeader
          closeButton
          className="absolute right-1 top-1 z-50 bg-white rounded-full p-1"
        />
        <Modal.ContentBody className="p-0 m-0">
          <If condition={!!mediaStream && !error}>
            <video ref={videoRef} onCanPlay={handleCanPlay} />
            <button
              onClick={handleClickPicture}
              className="absolute bottom-2 w-[40px] h-[40px] opacity-60 rounded-full
                left-2/4 -translate-x-2/4 -translate-y-0 bg-white shadow-[0_0_0_5px_gray]"
            />
            <canvas className="hidden" ref={canvasElementRef} />
          </If>
          <If condition={!!error}>
            <div>
              <p className="py-3 px-2 text-primaryBlue-700 font-bold">
                Please enable camera permission
              </p>
              <img
                src={`${imageBaseUrl}/img/camera-permissions.gif`}
                alt="permission-denied"
                className="w-full px-2"
              />
            </div>
          </If>
          <If condition={!mediaStream && !error}>
            <div className="p-24">Initializing camera...</div>
          </If>
        </Modal.ContentBody>
      </Modal.Content>
    </Modal>
  );
};

const CloseIcon = ({
  onClick,
  className,
}: {onClick: React.MouseEventHandler} & ComponentProps<"div">) => {
  return (
    <div
      className={twMerge(
        "absolute top-0 right-0 border-red-500 text-center text-xs w-5",
        "border-2 aspect-square cursor-pointer h-max text-red-500 rounded-full",
        className,
      )}
      onClick={onClick}
    >
      &#10005;
    </div>
  );
};

export const Camera = ({
  previewImage,
  children,
  onValueChange,
  className,
}: CameraProps) => {
  const [start, setStart] = useState<boolean>(false);
  const [image, setImage] = useState<string | undefined>();
  const [isPreviewImage, setIsPreviewImage] = useState(previewImage || "");

  useEffect(() => {
    if (image !== undefined) {
      onValueChange(image);
    }
  }, [image]);

  const handleCloseIconClick = () => {
    if (isPreviewImage) {
      setIsPreviewImage("");
    } else {
      setImage("");
    }
  };

  return (
    <CreateContext.Provider value={{setImage}}>
      <div className={twMerge("flex flex-col items-center", className)}>
        <If condition={!start && (!!image || !!isPreviewImage)}>
          <div className="relative">
            <CloseIcon
              onClick={handleCloseIconClick}
              className="top-1 right-1"
            />
            <img
              src={image || isPreviewImage}
              alt="captured-content"
              width={300}
            />
          </div>
        </If>
        <If condition={!(isPreviewImage || image)}>
          <div
            onClick={() => setStart(true)}
            className="text-primaryBlue-700 cursor-pointer h-auto min-h-[10rem] w-64 border-[1.5px]
                   border-dashed border-primary-300 bg-primary-50 flex flex-col justify-center items-center rounded-2xl"
          >
            <img
              width="70"
              src={`${imageBaseUrl}/img/maya/svg/upload-kyc-docs/selfie-kyc.svg`}
              alt="open-camera"
            />
            <p className="text-sm font-bold">Click to capture selfie</p>
          </div>
        </If>
        <If condition={start && !(image || isPreviewImage)}>
          <StartCamera onClose={() => setStart(false)} />
        </If>
        {children}
      </div>
    </CreateContext.Provider>
  );
};
